import React, { forwardRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getBookCustomerDetailAction,
  getPaymentBillAction,
  getAllBookingAction,
  getBookingDetailAction,
  updateContractNoAction,
} from "../../redux/booking/BookingSlice";
import { getPromotionDetail } from "../../redux/promotion/PromotionSlice";
import {
  getCompanyDetail,
  getDestinationDetail,
} from "../../redux/centerData/CenterDataSlice";
import { getYachtInformationDetail } from "../../redux/yachtInformation/YachtInformationSlice";
import { getYachtInfoDestinationPackageDetail } from "../../redux/yachtInfoDestinationPackage/YachtInfoDestinationPackageSlice";

const PrintableBill = forwardRef((props, ref) => {
  const { id_booking, price, rateType, date, vats } = props;

  const thStyle = { border: "none", padding: "6px", textAlign: "center" };
  const tdStyle = {
    border: "none",
    padding: "6px",
    textAlign: "left",
    verticalAlign: "top",
  };

  const dispatch = useDispatch();
  const todos = useSelector((state) => state);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(getBookCustomerDetailAction({})),
          dispatch(getPaymentBillAction({})),
          dispatch(getPromotionDetail({})),
          dispatch(getCompanyDetail({})),
          dispatch(getAllBookingAction({})),
          dispatch(getYachtInformationDetail({})),
          dispatch(getBookingDetailAction({})),
          dispatch(getYachtInfoDestinationPackageDetail({})),
          dispatch(getDestinationDetail({})),
        ]);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [dispatch]);

  const data = todos.Booking.bookCustomerDetailList.data || [];
  const found = data.find(
    (item) => Number(item.id_booking) === Number(id_booking)
  );

  const dataBill = todos.Booking.paymentBillList.data || [];
  const bill = dataBill.find(
    (item) => Number(item.id_booking) === Number(id_booking)
  );

  const allBookings = todos.Booking.bookingAllResult.data || [];
  const bookingMatched = allBookings.find(
    (item) => Number(item.id_booking) === Number(id_booking)
  );

  const bookingList = todos.Booking.Booking?.results || [];

  const bookingWithPromo = bookingList.find(
    (item) => Number(item.id_booking) === Number(id_booking)
  );

  const usePromoCode = bookingWithPromo?.use_promo || null;

  const promotionList = todos.PromotionSlice.Promotion?.results || [];

  const matchedPromotion = usePromoCode
    ? promotionList.find((promo) => promo.promotion_code === usePromoCode)
    : null;

  const total = Number(price) || 0;

  let subtotal, vat, totalWithVat;

  if (vats === 1) {
    // ราคาเป็น VAT รวม → แยก VAT ออก
    subtotal = total / 1.07;
    vat = total - subtotal;
    totalWithVat = total;
  } else {
    // ราคาไม่รวม VAT
    subtotal = total;
    vat = 0;
    totalWithVat = total;
  }

  let originalPrice = subtotal; // ราคาก่อนลด
  let discountAmount = 0;

  if (matchedPromotion) {
    const discountValue = Number(
      matchedPromotion.discount_value_promotion || 0
    );

    if (matchedPromotion.discount_promotion === "Percent") {
      // ลดเป็น %

      const percent = discountValue / 100;

      // subtotal = ราคาหลังลดแล้ว
      originalPrice = subtotal / (1 - percent);
      discountAmount = originalPrice - subtotal;

      originalPrice = subtotal + discountAmount;
    }

    if (matchedPromotion.discount_promotion === "THB") {
      // ลดเป็นบาท

      discountAmount = discountValue;
      originalPrice = subtotal + discountAmount;
    }
  }

  useEffect(() => {
    if (
      found &&
      bill &&
      id_booking &&
      bookingMatched &&
      !bookingMatched.contract_no
    ) {
      const contract_no = generateContractNo(found.id_bookCustomerdetail);
      dispatch(updateContractNoAction({ id_booking, contract_no }));
    }
  }, [found, bill, id_booking, bookingMatched, dispatch]);

  if (loading) return <div>Loading...</div>;
  if (!found || !bill) return <div>ไม่พบข้อมูลบิลหรือผู้จอง</div>;

  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB").replace(/\//g, "-");

  const bookingNo = bill?.booking_bill_no || "";
  const dateStr = bookingNo.substring(3, 11);
  const formattedDates =
    dateStr.length === 8
      ? `${dateStr.substring(6, 8)}-${dateStr.substring(
        4,
        6
      )}-${dateStr.substring(0, 4)}`
      : "N/A";

  const companyData =
    todos.CenterDataSlice.Company?.results?.find(
      (item) => item.id_company === "0000000176"
    ) || {};

  const formatAddressWithLineBreak = (address) => {
    if (!address || typeof address !== "string") return "N/A";
    const lastCommaIndex = address.lastIndexOf(",");
    if (lastCommaIndex === -1) return address;
    return (
      <>
        {address.slice(0, lastCommaIndex + 1)}
        <br />
        {address.slice(lastCommaIndex + 1).trim()}
      </>
    );
  };

  function generateContractNo(customerIdRaw) {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const customerIdTrimmed = String(Number(customerIdRaw));
    return `${yyyy}${mm}${dd}${customerIdTrimmed}`;
  }

  const idYachtInfo = bookingMatched?.id_yachtInfo;
  const allYachts = todos.YachtInformationSlice.YachtInformation?.results || [];
  const yachtMatched = allYachts.find(
    (item) => Number(item.id_yachtInfo) === Number(idYachtInfo)
  );
  const yachtType = yachtMatched?.value_yachtTypes || "-";

  const formatCurrency = (num) =>
    Number(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const bookingDetailData = todos.Booking.bookingDetailResult.data || [];
  const detailMatched = bookingDetailData.find(
    (item) => Number(item.id_booking) === Number(id_booking)
  );

  let destinationName = "N/A";
  if (detailMatched) {
    const idDestinationPackage = detailMatched.id_destination_package;
    const allDestinationPackages =
      todos.YachtInfoDestinationPackageSlice.YachtInfoDestinationPackage
        ?.results || [];
    const matchedPackage = allDestinationPackages.find(
      (pkg) =>
        Number(pkg.id_destination_package) === Number(idDestinationPackage)
    );
    const destinationIds = (matchedPackage?.destination_included || "")
      .split(",")
      .map((id) => id.trim());
    const allDestinations = todos.CenterDataSlice.Destination.results || [];
    const matchedDestinations = allDestinations.filter((dest) =>
      destinationIds.includes(dest.id_destination)
    );
    destinationName = matchedDestinations
      .map((dest) => dest.name_destination_en)
      .join(", ");
  }

  const th = {
    border: "1px solid #000",
    padding: "6px",
    textAlign: "center",
    fontWeight: "bold",
  };
  const td = {
    border: "1px solid #000",
    padding: "6px",
    verticalAlign: "top",
  };

  const calculateDays = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return 1;

    // ล้างช่องว่าง
    const cleanStr = dateStr.trim();

    // แยกด้วย "-" หรือ ","
    let parts = cleanStr
      .split(/[-,]/)
      .map((s) => s.trim())
      .filter(Boolean);

    // ถ้ามีวันเดียว
    if (parts.length === 1) return 1;

    // ถ้ามี 2 วัน ให้คำนวณต่างวัน
    if (parts.length >= 2) {
      const [startStr, endStr] = parts;

      // แปลง "." → "/" เพื่อกัน format ต่างกัน
      const startDate = new Date(startStr.replace(/\./g, "/"));
      const endDate = new Date(endStr.replace(/\./g, "/"));

      if (isNaN(startDate) || isNaN(endDate)) return 1;

      const diffTime = endDate - startDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays > 0 ? diffDays : 1;
    }

    return 1;
  };

  const daysDiff = calculateDays(date);

  let durationValue = daysDiff;
  let durationUnit = "Day(s)";

  if (daysDiff >= 30) {
    durationValue = Math.round(daysDiff / 30);
    durationUnit = "Month(s)";
  }

  const numberToThaiText = (number) => {
    number = parseFloat(number);
    if (isNaN(number)) return "";

    const numberText = [
      "ศูนย์",
      "หนึ่ง",
      "สอง",
      "สาม",
      "สี่",
      "ห้า",
      "หก",
      "เจ็ด",
      "แปด",
      "เก้า",
    ];
    const positionText = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน", "ล้าน"];

    const toThai = (num) => {
      let result = "";
      const numStr = num.toString();
      for (let i = 0; i < numStr.length; i++) {
        const digit = parseInt(numStr.charAt(i));
        const pos = numStr.length - i - 1;

        if (digit === 0) continue;

        if (pos === 0 && digit === 1 && numStr.length > 1) {
          result += "เอ็ด";
        } else if (pos === 1 && digit === 2) {
          result += "ยี่";
        } else if (pos === 1 && digit === 1) {
          result += "";
        } else {
          result += numberText[digit];
        }

        result += positionText[pos];
      }
      return result;
    };

    const parts = number.toFixed(2).split(".");
    const baht = parseInt(parts[0]);
    const satang = parseInt(parts[1]);

    let bahtText = toThai(baht) + "บาท";
    let satangText = satang > 0 ? toThai(satang) + "สตางค์" : "ถ้วน";

    return bahtText + satangText;
  };

  return (
    <div ref={ref} className="arial-text-box">
      <h3 className="text-center mb-20">
        {vats === 1 ? (
          <>
            RECEIPT / TAX INVOICE
            <br />
            ใบเสร็จรับเงิน/ใบกำกับภาษี
          </>
        ) : (
          <>
            RECEIPT
            <br />
            ใบเสร็จรับเงิน
          </>
        )}
      </h3>
      {/* --- Company Info --- */}
      <div
        className="flex-between-start arial-text"
      >
        {/* ฝั่งข้อมูลบริษัท */}
        <div
          className="content-box"
        >
          <p className="m0">
            <strong className="fs-20">
              {companyData.name_company_en || "N/A"}
            </strong>{" "}
            <br /> {companyData.name_company || "N/A"}
          </p>
          <p className="m0">
            {formatAddressWithLineBreak(companyData.address_en)} <br />{" "}
            {formatAddressWithLineBreak(companyData.address)}
          </p>
          <p className="m0">
            <strong>Tel:</strong> {companyData.phone_no || "-"}
            &nbsp;&nbsp;&nbsp;
            <strong>Fax:</strong> {companyData.fax_no || "-"}
          </p>
          <p className="m0">
            <strong>Tax ID :</strong> {companyData.id_tax || "-"} &nbsp;&nbsp;
            <strong>Branch No.:</strong> {companyData.name_branch || "-"}
          </p>

          <p className="m0">
            <strong>เลขประจำตัวผู้เสียภาษีอากร:</strong>{" "}
            {companyData.id_tax || "-"} สาขาประกอบการ สำนักงานใหญ่
          </p>
        </div>

        {/* ฝั่งโลโก้ + Original */}
        <div
          className="flex-column-end-start"
        >
          {companyData.logo_company_url && (
            <img
              src={companyData.logo_company_url}
              alt={companyData.name_logo_company || "Logo"}
              className="w-390"
            />
          )}
          <div
            className="badge-box"
          >
            ต้นฉบับ/Original
          </div>
        </div>
      </div>
      <hr className="my-20" />
      {/* --- Customer & Receipt Info --- */}
      <div className="flex-space-between">
        <div
          className="half-card"
        >
          <p>
            <strong>รหัสลูกค้า/Customer No.:</strong>{" "}
            {found.id_bookCustomerdetail}
          </p>
          <p>
            <strong>ชื่อลูกค้า/Customer Name:</strong> {found.firstName}{" "}
            {found.lastName}
          </p>
          <p>
            <strong>สาขา/Branch:</strong> -
          </p>
          <p>
            <strong>ที่อยู่/Address:</strong> {found.address}
          </p>
          <p>
            <strong>เบอร์ติดต่อ/Tel:</strong> {found.mobileNo}
          </p>
          {/* <p>
            <strong>เลขประจำตัวผู้เสียภาษีอากร/Tax ID:</strong>{" "}
            {found.idPassport}
          </p> */}
        </div>
        <div className="w-48">
          <div
            className="full-box"
          >
            <p>
              <strong>ใบเสร็จรับเงิน/Receipt No.:</strong>{" "}
              {`RCPT${new Date()
                .toISOString()
                .slice(0, 10)
                .replace(/-/g, "")}${bill.booking_bill_no?.slice(-4)}`}
            </p>
            <p>
              <strong>วันที่ออกใบเสร็จรับเงิน/Issue Date:</strong>{" "}
              {formattedDate}
            </p>
            <p>
              <strong>สัญญาเลขที่/Contract No.:</strong>{" "}
              {generateContractNo(found.id_bookCustomerdetail, 1)}
            </p>
            <p>
              <strong>วันที่ชำระเงิน/Payment Date:</strong> {formattedDates}
            </p>
          </div>
          <div className="full-border-box">
            <p>
              <strong>รูปแบบการชำระเงิน/Payment Type:</strong>{" "}
              {bill.payment_type}
            </p>
            <p>
              <strong>กำหนดชำระ/Credit Term:</strong> -
            </p>
          </div>
        </div>
      </div>

      {/* --- Trip Detail Table --- */}
      <div
        className="mt-30 arial-sm"
      >
        {/* ชื่อโปรเจค */}
        <div className="mb-8">
          <strong>ชื่อโปรเจค/Project Name : </strong> Take My Yacht
        </div>

        {/* ตารางรายการ */}
        <table
          className="table-bordered"
        >
          <thead>
            <tr className="bg-light text-center">
              <th
                className="table-cell-small"
              >
                รายการ
                <br />
                No.
              </th>
              <th
                className="table-cell-20"
              >
                ชื่อสินค้า
                <br />
                Product Name
              </th>
              <th
                className="table-cell-25"
              >
                คำอธิบาย
                <br />
                Description
              </th>
              <th
                className="table-cell-8"
              >
                จำนวน
                <br />
                Qty
              </th>
              <th
                className="table-cell-10"
              >
                หน่วย
                <br />
                Unit
              </th>
              <th
                className="table-cell-12"
              >
                ราคาต่อหน่วย
                <br />
                Price per Unit
              </th>
              <th
                className="table-cell-10"
              >
                ส่วนลด
                <br />
                Discount
              </th>
              <th
                className="table-cell-10"
              >
                ราคา
                <br />
                Price
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="text-center">
              <td className="table-cell">1</td>
              <td className="table-cell">
                <div>
                  {destinationName} ({rateType})
                </div>
              </td>
              <td
                className="table-cell-left"
              >
                <div>Travel date-time : {date}</div>
                <div>Yacht Type : {yachtType}</div>
                <div>
                  Adult(s): {bookingMatched.adult || 0}
                  &nbsp;&nbsp; Child(s): {bookingMatched.child || 0}
                  &nbsp;&nbsp; Infant(s): {bookingMatched.infant || 0}
                </div>
              </td>
              <td className="table-cell">
                {durationValue}
              </td>
              <td className="table-cell">
                {durationUnit}
              </td>
              <td className="table-cell">
                {formatCurrency(originalPrice)}
              </td>
              <td className="table-cell">
                {formatCurrency(discountAmount)}
              </td>
              <td className="p-5">{formatCurrency(subtotal)}</td>
            </tr>
          </tbody>
        </table>

        {/* รวมจำนวนเงินและหมายเหตุ */}
        <div
          className="flex-end mt-neg-1"
        >
          {/* ฝั่งซ้าย */}
          <div className="flex-1">
            <p className="mt-1 mb-2">
              จำนวนเงินรวมทั้งสิ้น/Total : {numberToThaiText(total)}
            </p>
            <p className="mb-2">
              โปรโมชั่น/Promotion : {
                matchedPromotion
                  ? `${matchedPromotion.name_promotion} (${matchedPromotion.promotion_code})`
                  : "-"
              }
            </p>
            <p className="mb-2">
              หมายเหตุ/Remark(s) :
            </p>
          </div>

          {/* ฝั่งขวา (กรอบสีแดง) */}
          <div
            className="w-280"
          >
            <table
              className="table-full-border-right"
            >
              <tbody>
                <tr>
                  <td
                    className="cell-right"
                  >
                    รวมเป็นเงิน/Subtotal :
                  </td>
                  <td
                    className="cell-right-bordered"
                  >
                    {formatCurrency(subtotal)}
                  </td>
                </tr>

                <tr>
                  <td
                    className="cell-right"
                  >
                    ภาษีมูลค่าเพิ่ม/VAT (7%) :
                  </td>
                  <td
                    className="cell-right-bordered"
                  >
                    {formatCurrency(vat)}
                  </td>
                </tr>
                <tr>
                  <td
                    className="cell-right"
                  >
                    จำนวนเงินรวมทั้งสิ้น/Total :
                  </td>
                  <td
                    className="cell-right-bordered"
                  >
                    {formatCurrency(total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- Note --- */}
      <div
        className="flex-space-between-bordered mt-40 text-center-sm"
      >
        {/* ช่องซ้าย: ผู้รับเงิน */}
        <div
          className="flex-item-border-right"
        >
          <p className="m0-text-left">
            {companyData.name_company || "N/A"}
          </p>
          <div
            className="footer-center-end"
          >
            <p style={{marginTop: "65px"}}>
              --------------------------------------------------------------
            </p>
            <p className="mt0">ผู้รับเงิน</p>
          </div>
        </div>

        {/* ช่องกลาง: ตราประทับบริษัท */}
        <div
          className="flex-1-border-right"
        >
          <p className="m0-text-left">ตราประทับบริษัท</p>
          <div
            className="flex-center-60h"
          ></div>
        </div>

        {/* ช่องขวา: ข้อความอิเล็กทรอนิกส์ */}
        <div
          className="flex-center-cell"
        >
          <p className="m0">
            เอกสารนี้จัดทำและส่งข้อมูลในรูปแบบอิเล็กทรอนิกส์
            <br />
            This document is prepared and submitted electronically to the
            <br />
            Revenue Department
          </p>
        </div>
      </div>
    </div>
  );
});

export default PrintableBill;
